import { User } from '../interfaces/user.interface';
import { AccountService } from './account.service';
import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { TokenStorageService } from './helpers/token.service';
import { TokenRefreshService } from './token-refresh.service';
import { environment } from '../../../environments/environment';

import { ToastrService } from 'ngx-toastr';
import { RolesEnum } from '../enums/roles.enum';

@Injectable()
export class SocketService implements OnDestroy {
  subscribersCounter: Record<string, number> = {};
  eventObservables$: Record<string, Observable<any>> = {};
  currentUser: User;

  EVENTS = {
    connect: 'connect',
    reconnect: 'reconnect',
    startTraining: 'started-session',
    completeTraining: 'completed-session',
    pauseTraining: 'paused-session',
    resumeTraining: 'resumed-session',
    startSession: 'started-session-scenario',
    completeSession: 'completed-session-scenario',
    startSessionItem: 'started-session-scenario-item',
    completeSessionItem: 'completeSessionItem',
    answerSessionItem: 'answered-session-scenario-item',
    addToTeam: 'set-session-team-for-participant',
    setTeamLeader: 'set-team-lead',
    addMessage: 'created-session-chat-message',
    editMessage: 'updated-session-chat-message',
    deleteMessage: 'deleted-session-chat-message',
    addIncidentComment: 'created-session-incident-comment',
    editIncidentComment: 'updated-session-incident-comment',
    deleteIncidentComment: 'deleted-session-incident-comment',
    approveParticipation: 'approved-participation',
    logoutUser: 'logout',
    userRegistered: 'registered-participant',
    approveUser: 'approved-participant',
    createSessionItem: 'created-session-scenario-item',
    addedUsersToTraining: 'assigned-participant-to-session',
    updateSessionItem: 'updated-session-scenario-item',
    trainingOverviewGenerated: 'generated-training-overview-for-instructor',
    assessmentOverviewGenerated: 'generated-assessment-overview-for-instructor',
    generatedCompanyUsageSummaryReportEvent:
      'generated-company-usage-summary-report',
    sentParticipantTrainingOverview: 'sent-training-overview-for-participant',
    resumedSessionScenarioItem: 'resumed-session-scenario-item',
    pausedSessionScenarioItem: 'paused-session-scenario-item',
    deletedSessionScenarioItem: 'deleted-session-scenario-item',
    generatedScenarioExport: 'generated-scenario-export',
    usersImportCompleted: 'users-import-completed',
    createdSession: 'created-session',
    updatedSession: 'updated-session',
    sessionTerminated: 'session-terminated',
    aiChatGenerationProgress: 'ai-chat-generation-progress',
    scheduledSessionScenarioItemAutoAdvance:
      'scheduled-session-scenario-item-auto-advance',
    sessionScenarioItemAnswerTallyUpdated:
      'updated-session-scenario-item-answer-tally',
  };

  private eventData$ = new Subject<any>();
  private connectionStatus$ = new Subject<any>();
  subscriptions = [];
  client: any;
  pusher: any;
  channel: any;
  private sessionAdminViewChannel: any = null;
  private sessionAdminViewChannelName: string | null = null;
  private activeSessionAdminViewId: string | number | null = null;
  private isReconnecting = false;
  private isRefreshingBroadcastAuth = false;

  constructor(
    public accountService: AccountService,
    private tokenService: TokenStorageService,
    private tokenRefreshService: TokenRefreshService,
    @Inject(DOCUMENT) private document: any,
    private toastrService: ToastrService,
  ) {
    this.initSocket();
  }

  ngOnDestroy() {
    if (this.client) {
      this.client.disconnect();
    }

    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  initSocket() {
    this.currentUser = this.accountService.getAccount();
    if (this.currentUser) {
      this.connectToSocket();
    }
  }

  /** Call after HTTP access-token refresh so private channels re-auth with the new token. */
  reconnectAfterTokenRefresh(): void {
    if (!this.currentUser || this.isReconnecting) {
      return;
    }
    this.isReconnecting = true;
    this.reconnectSocket();
  }

  private connectToSocket() {
    // @ts-ignore
    const PusherJS = require('pusher-js');
    const config = {
      cluster: '',
      wsHost: this.document.location.hostname,
      wsPort: this.document.location.port,
      wssPort: this.document.location.port,
      httpHost: this.document.location.hostname,
      httpPort: this.document.location.port,
      httpsPort: this.document.location.port,
      forceTLS: this.document.location.protocol === 'https:',
      encrypted: true,
      disableStats: true,
      // Keep traffic on our host only — sockjs/xhr fallbacks hit sockjs.pusher.com (blocked by CSP).
      enabledTransports: ['ws', 'wss'],
      channelAuthorization: {
        transport: 'ajax' as const,
        endpoint: '/broadcasting/auth',
        // Always read the latest access token (static headers become stale after refresh).
        headersProvider: () => this.getBroadcastAuthHeaders(),
        customHandler: (
          params: { socketId: string; channelName: string },
          callback: (error: Error | null, authData: any) => void,
        ) => {
          void this.authorizeChannel(params, callback);
        },
      },
    };

    this.client = new PusherJS(environment.ws.key, config);

    this.client.connection.bind('connected', () => {
      this.setConnectionStatus('Connected');
      this.isReconnecting = false;
    });

    this.client.connection.bind('connecting', () => {
      this.setConnectionStatus('Connecting');
    });

    this.client.connection.bind('unavailable', () => {
      this.setConnectionStatus('Disconnected');
    });

    this.client.connection.bind('failed', () => {
      this.setConnectionStatus('Disconnected');
    });

    this.client.connection.bind('error', (err: any) => {
      console.error('WebSocket connection error:', err);
      if (err.code === 4004) {
        void this.handleAuthError();
      }
    });

    this.client.connection.bind('disconnected', () => {
      this.setConnectionStatus('Disconnected');
    });

    this.subscribe();
    if (
      this.currentUser.role === RolesEnum.admin ||
      this.currentUser.role === RolesEnum.instructor
    ) {
      this.subscribeRoom('admin-view');
    }
    if (this.currentUser.role === RolesEnum.participant) {
      this.subscribeRoom('participant-view');
    }
    if (this.activeSessionAdminViewId != null) {
      this.subscribeSessionAdminView(this.activeSessionAdminViewId);
    }
  }

  private getBroadcastAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    };
    const token = this.tokenService.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  private async authorizeChannel(
    params: { socketId: string; channelName: string },
    callback: (error: Error | null, authData: any) => void,
    isRetry = false,
  ): Promise<void> {
    const token = this.tokenService.getToken();
    if (!token) {
      callback(new Error('No auth token available'), null);
      return;
    }

    try {
      const response = await fetch('/broadcasting/auth', {
        method: 'POST',
        headers: this.getBroadcastAuthHeaders(),
        body: new URLSearchParams({
          socket_id: params.socketId,
          channel_name: params.channelName,
        }),
      });

      if (response.status === 401) {
        if (!isRetry) {
          const refreshed = await this.refreshAccessToken();
          if (refreshed) {
            await this.authorizeChannel(params, callback, true);
            return;
          }
        }
        callback(new Error('Broadcasting auth unauthorized'), null);
        void this.handleAuthError();
        return;
      }

      if (!response.ok) {
        callback(
          new Error(`Broadcasting auth failed with status ${response.status}`),
          null,
        );
        return;
      }

      const authData = await response.json();
      callback(null, authData);
    } catch (error) {
      callback(
        error instanceof Error ? error : new Error('Broadcasting auth failed'),
        null,
      );
    }
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (this.isRefreshingBroadcastAuth) {
      return false;
    }
    if (!this.tokenService.getRefreshToken()) {
      return false;
    }

    this.isRefreshingBroadcastAuth = true;
    try {
      await firstValueFrom(this.tokenRefreshService.refreshToken());
      return !!this.tokenService.getToken();
    } catch (error) {
      console.error('Failed to refresh token for broadcasting auth:', error);
      return false;
    } finally {
      this.isRefreshingBroadcastAuth = false;
    }
  }

  private async handleAuthError() {
    if (this.isReconnecting) {
      return;
    }
    console.log('Handling WebSocket authentication error');
    this.isReconnecting = true;
    try {
      const refreshed = await this.refreshAccessToken();
      if (!refreshed && !this.tokenService.getToken()) {
        this.isReconnecting = false;
        this.handleLogout();
        return;
      }
      this.reconnectSocket();
    } catch (error) {
      console.error('Failed to refresh token for WebSocket:', error);
      this.isReconnecting = false;
      this.handleLogout();
    }
  }

  private handleLogout() {
    const user = this.accountService.getAccount();
    if (user) {
      this.unSubscribe(user.id);
    }
    localStorage.clear();
    sessionStorage.clear();
    globalThis.location.href = '/auth/login';
  }

  subscribe() {
    if (this.currentUser && this.client) {
      this.channel = this.client.subscribe(
        `private-user.${this.currentUser.id}`,
      );
      this.subscribeForAll();
    }
  }
  subscribeRoom(room: string) {
    if (this.currentUser && this.client) {
      this.channel = this.client.subscribe(
        `private-user.${this.currentUser.id}.${room}`,
      );
      this.subscribeForAll();
    }
  }

  unSubscribe(userId) {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    if (this.client) {
      this.client.unsubscribe(`private-user.${userId}`);
    }
  }
  unSubscribeRoom(userId, room) {
    if (this.client) {
      this.client.unsubscribe(`private-user.${userId}.${room}`);
    }
  }

  subscribeSessionAdminView(sessionId: string | number): void {
    if (sessionId == null || sessionId === '') {
      return;
    }

    // Keep id even if client is not ready yet so reconnect can subscribe.
    this.activeSessionAdminViewId = sessionId;

    if (!this.client) {
      return;
    }

    const channelName = `private-session.${sessionId}.admin-view`;

    if (
      this.sessionAdminViewChannelName === channelName &&
      this.sessionAdminViewChannel
    ) {
      return;
    }

    this.unsubscribeSessionAdminView(undefined, true);

    this.activeSessionAdminViewId = sessionId;
    this.sessionAdminViewChannelName = channelName;
    this.sessionAdminViewChannel = this.client.subscribe(channelName);

    const event = this.EVENTS.sessionScenarioItemAnswerTallyUpdated;
    this.sessionAdminViewChannel.bind(event, (res) => {
      const data = {
        event,
        data: this.parseEventPayload(res),
      };
      console.log(data);
      this.setEventData(data);
    });
  }

  unsubscribeSessionAdminView(
    sessionId?: string | number,
    keepActiveSessionId = false,
  ): void {
    const channelName =
      sessionId != null && sessionId !== ''
        ? `private-session.${sessionId}.admin-view`
        : this.sessionAdminViewChannelName;

    if (this.client && channelName) {
      this.client.unsubscribe(channelName);
    }

    this.sessionAdminViewChannel = null;
    this.sessionAdminViewChannelName = null;
    if (!keepActiveSessionId) {
      this.activeSessionAdminViewId = null;
    }
  }

  setEventData(data) {
    this.eventData$.next(data);
  }

  getEventData() {
    return this.eventData$.asObservable();
  }

  getConnectionStatus() {
    return this.connectionStatus$.asObservable();
  }

  setConnectionStatus(data) {
    this.connectionStatus$.next(data);
  }

  subscribeForAll() {
    Object.values(this.EVENTS).forEach((event) => {
      this.subscriptions.push(
        this.channel.bind(event, (res) => {
          const data = {
            event,
            data: this.parseEventPayload(res),
          };
          console.log(data);
          if (data.event === this.EVENTS.usersImportCompleted) {
            this.toastrService.info(
              `Created: ${data.data.created}<br>Failed: ${data.data.failed} <br>Total: ${data.data.total}`,
              'Imported CSV users:',
              { enableHtml: true },
            );
          }
          this.setEventData(data);
        }),
      );
    });
  }

  private parseEventPayload(res: any): any {
    if (typeof res !== 'string') {
      return res;
    }

    try {
      return JSON.parse(res);
    } catch {
      return res;
    }
  }

  private reconnectSocket() {
    if (this.client) {
      this.client.disconnect();
    }

    this.sessionAdminViewChannel = null;
    this.sessionAdminViewChannelName = null;
    this.channel = null;
    this.subscriptions = [];

    setTimeout(() => {
      this.currentUser = this.accountService.getAccount() || this.currentUser;
      if (this.currentUser) {
        this.connectToSocket();
      } else {
        this.isReconnecting = false;
      }
    }, 500);
  }
}
